import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SiteContentContext = createContext();

// Helper to detect language from URL path
const getLanguageFromPath = (pathname) => {
  // Debug
  console.log('[getLanguageFromPath] pathname:', pathname);
  
  // If path starts with /en/, it's English
  if (pathname.startsWith('/en/') || pathname === '/en') {
    console.log('[getLanguageFromPath] returning: en');
    return 'en';
  }
  // If path starts with /de/, it's German
  if (pathname.startsWith('/de/') || pathname === '/de') {
    console.log('[getLanguageFromPath] returning: de');
    return 'de';
  }
  // Otherwise, it's Bulgarian (default)
  console.log('[getLanguageFromPath] returning: bg (default)');
  return 'bg';
};

// Helper to get path without language prefix
const getPathWithoutLang = (pathname) => {
  if (pathname.startsWith('/en/')) {
    return pathname.slice(3); // Remove '/en' prefix
  }
  if (pathname === '/en') {
    return '/';
  }
  if (pathname.startsWith('/de/')) {
    return pathname.slice(3); // Remove '/de' prefix
  }
  if (pathname === '/de') {
    return '/';
  }
  return pathname;
};

// Helper to add language prefix to path
const getLocalizedPath = (path, lang) => {
  if (lang === 'en') {
    if (path === '/') return '/en';
    return `/en${path}`;
  }
  if (lang === 'de') {
    if (path === '/') return '/de';
    return `/de${path}`;
  }
  return path;
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Clear old language localStorage (migration from old system)
  useEffect(() => {
    if (localStorage.getItem('language')) {
      localStorage.removeItem('language');
    }
  }, []);
  
  // Get language from URL only (not from localStorage)
  const language = getLanguageFromPath(location.pathname);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await apiClient.get(`${API}/site-content`);
        const contentMap = {};
        Object.entries(res.data).forEach(([key, values]) => {
          contentMap[key] = { key, ...values };
        });
        setContent(contentMap);
      } catch (error) {
        console.error('Failed to load site content:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Get content by key, returns value based on current language
  const getContent = (key, fallback = '') => {
    const item = content[key];
    if (!item) return fallback;
    
    let value;
    if (language === 'bg') {
      value = item.value_bg;
    } else if (language === 'de') {
      value = item.value_de;
      // For German, if no translation exists, use the provided fallback instead of English
      if (!value && fallback) return fallback;
    } else {
      value = item.value_en;
    }
    return value || item.value_en || fallback;
  };

  // Shorthand alias
  const t = getContent;

  // Switch language - navigates to the equivalent page in the other language
  const switchLanguage = (newLang) => {
    const currentPath = getPathWithoutLang(location.pathname);
    const newPath = getLocalizedPath(currentPath, newLang);
    console.log('switchLanguage:', { currentPath, newLang, newPath, fullPath: newPath + location.search + location.hash });
    navigate(newPath + location.search + location.hash);
  };

  // Get localized link path
  const localizedPath = (path) => {
    return getLocalizedPath(path, language);
  };

  return (
    <SiteContentContext.Provider value={{ 
      content, 
      language, 
      switchLanguage,
      localizedPath,
      getContent, 
      t,
      isLoading 
    }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within a SiteContentProvider');
  }
  return context;
};

// Static translations that don't need to be editable (navigation, buttons, etc.)
export const staticTranslations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About Us',
      services: 'Services',
      fleet: 'Fleet',
      destinations: 'Destinations',
      whyChooseUs: 'Why Choose Us',
      ldmCalculator: 'Cargo Calculator',
      careers: 'Careers',
      news: 'News',
      faq: 'FAQ',
      contact: 'Contact',
      getQuote: 'Get Quote',
    },
    common: {
      learnMore: 'Learn More',
      readMore: 'Read More',
      applyNow: 'Apply Now',
      submit: 'Submit',
      sending: 'Sending...',
      loading: 'Loading...',
      backToHome: 'Back to Home',
      noResults: 'No results found',
    },
  },
  bg: {
    nav: {
      home: 'Начало',
      about: 'За нас',
      services: 'Услуги',
      fleet: 'Флот',
      destinations: 'Дестинации',
      whyChooseUs: 'Защо нас',
      ldmCalculator: 'Калкулатор',
      careers: 'Кариери',
      news: 'Новини',
      faq: 'Въпроси',
      contact: 'Контакт',
      getQuote: 'Оферта',
    },
    common: {
      learnMore: 'Научи повече',
      readMore: 'Прочети повече',
      applyNow: 'Кандидатствай',
      submit: 'Изпрати',
      sending: 'Изпращане...',
      loading: 'Зареждане...',
      backToHome: 'Към началото',
      noResults: 'Няма резултати',
    },
  },
  de: {
    nav: {
      home: 'Startseite',
      about: 'Über uns',
      services: 'Dienstleistungen',
      fleet: 'Flotte',
      destinations: 'Transportziele',
      whyChooseUs: 'Warum wir',
      ldmCalculator: 'Frachtrechner',
      careers: 'Karriere',
      news: 'Nachrichten',
      faq: 'FAQ',
      contact: 'Kontakt',
      getQuote: 'Angebot',
    },
    common: {
      learnMore: 'Mehr erfahren',
      readMore: 'Weiterlesen',
      applyNow: 'Jetzt bewerben',
      submit: 'Absenden',
      sending: 'Wird gesendet...',
      loading: 'Laden...',
      backToHome: 'Zur Startseite',
      noResults: 'Keine Ergebnisse gefunden',
    },
  },
};

// Hook to get static translation
export const useStaticTranslation = () => {
  const { language } = useSiteContent();
  
  const st = (key) => {
    const keys = key.split('.');
    let value = staticTranslations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };
  
  return { st, language };
};
